import { create } from 'zustand';
import {
  requestPhoneOtp,
  confirmOtpAndGetIdToken,
  signOutFirebase,
} from './firebase';
import { apiFetch, type ApiError } from './api-client';
import { rememberPhone } from './last-phone';
import type { ConfirmationResult } from 'firebase/auth';

// Mirrors the backend's toPublicDriver() shape (apps/api/src/modules/driver-auth/driver-public-fields.ts).
// We deliberately do NOT carry *Url fields — the backend strips them to avoid
// shipping base64 data-URIs (see GH#98). Use hasPhoto / hasDl / etc. instead.
export type DriverProfile = {
  id: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  phone: string;
  // Secondary phone (E.164) when ops entered one in connect.fleetup.in.
  // Used to pre-fill the signup wizard's optional mobile field.
  secondaryPhone: string | null;
  // KYC string fields, used to pre-fill the signup wizard so the driver only
  // confirms numbers ops already entered. Photo data lives on a separate
  // streaming endpoint (/v1/drivers/:id/photo) per the GH#98 pattern.
  drivingLicenseNumber: string | null;
  drivingLicenseExpiry: string | null; // YYYY-MM-DD
  aadhaarNumber: string | null;
  panNumber: string | null;
  ownerOrgId: string;
  // Operator name for the profile-hero subtitle. Null on legacy responses
  // produced before this field was added — UI falls back to "Driver" only.
  ownerOrgName: string | null;
  // Operator's support phone (connect.fleetup.in → Company → Contacts).
  // Drives the Help page helpline; null on legacy responses / when unset.
  ownerOrgSupportPhone: string | null;
  branchId: string | null;
  status: string;
  hasPhoto: boolean;
  hasDl: boolean;
  hasAadhaar: boolean;
  hasPan: boolean;
  /** Driver row creation timestamp (ISO 8601). Used by the profile "Driver since" row. */
  createdAt: string;
  // Per-doc last-modified timestamps. Drive the local cache's staleness
  // detection. Null on legacy rows pre-existing the corresponding migration;
  // the viewer treats null as "no newer data exists, cache wins".
  photoUpdatedAt:          string | null;
  drivingLicenseUpdatedAt: string | null;
  aadhaarUpdatedAt:        string | null;
  panUpdatedAt:            string | null;
};

type AuthState = {
  status: 'loading' | 'unauthenticated' | 'authenticated';
  driver: DriverProfile | null;
  pending: { phone: string; confirmation: ConfirmationResult } | null;
  error: string | null;

  loadMe: () => Promise<void>;
  requestOtp: (phoneE164: string) => Promise<void>;
  verifyOtp: (code: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
};

export const useAuth = create<AuthState>((set, get) => ({
  status: 'loading',
  driver: null,
  pending: null,
  error: null,

  loadMe: async () => {
    set({ status: 'loading', error: null });
    try {
      // Try refresh first to extend session if cookie present.
      // Surface 403s (FEATURE_FLAG_DISABLED / DRIVER_INACTIVE) immediately
      // instead of swallowing them — otherwise the user bounces to login,
      // enters OTP, and then sees the same error one round-trip later.
      try {
        await apiFetch('/driver-auth/refresh', { method: 'POST' });
      } catch (e) {
        const err = e as ApiError;
        if (err.status === 403) {
          set({ status: 'unauthenticated', driver: null, error: err.message });
          return;
        }
        // 401 / network: no session, fall through to /me which will 401 too
      }
      const r = await apiFetch<{ driver: DriverProfile }>('/driver-auth/me');
      set({ status: 'authenticated', driver: r.driver, error: null });
    } catch (e) {
      set({ status: 'unauthenticated', driver: null });
    }
  },

  requestOtp: async (phoneE164) => {
    set({ error: null });
    try {
      const confirmation = await requestPhoneOtp(phoneE164);
      set({ pending: { phone: phoneE164, confirmation } });
      // Remember for prefill so a future dropped-session recovery is one tap (GH #502).
      rememberPhone(phoneE164);
    } catch (e) {
      // Map Firebase auth error codes to actionable copy. The fallback retains
      // the original "check the number" wording for unknown errors but appends
      // the Firebase error code so we can debug from screenshots without
      // needing remote console access.
      const code = (e as { code?: string })?.code;
      // eslint-disable-next-line no-console
      console.error('[auth.requestOtp] Firebase error', code, e);
      let msg = 'Could not send OTP. Check the number and try again.';
      if (code === 'auth/unauthorized-domain') {
        msg = 'This site is not yet authorised for sign-in. Tell your operator.';
      } else if (code === 'auth/too-many-requests') {
        msg = 'Too many attempts. Wait a few minutes and try again.';
      } else if (code === 'auth/captcha-check-failed' || code === 'auth/missing-verification-id') {
        msg = 'Verification failed. Refresh the page and try again.';
      } else if (code === 'auth/invalid-app-credential') {
        msg = 'App is misconfigured. Tell your operator (auth/invalid-app-credential).';
      } else if (code === 'auth/quota-exceeded') {
        msg = 'SMS quota reached. Try again later.';
      } else if (code) {
        msg = `Could not send OTP (${code}). Try again or tell your operator.`;
      }
      set({ error: msg });
      throw e;
    }
  },

  verifyOtp: async (code) => {
    const pending = get().pending;
    if (!pending) {
      set({ error: 'OTP session expired. Restart login.' });
      return;
    }
    set({ error: null });
    try {
      const { idToken } = await confirmOtpAndGetIdToken(pending.confirmation, code);
      const r = await apiFetch<{ driver: DriverProfile }>('/driver-auth/login', {
        method: 'POST',
        body: JSON.stringify({ idToken }),
      });
      set({ status: 'authenticated', driver: r.driver, pending: null, error: null });
    } catch (e) {
      // Backend always populates message via codeToMessage(), so apiErr.message
      // is the right user-facing copy for any HTTP error. Network errors come
      // through with status:0 + a generic message — that's also what we want
      // to show. Only fall back if message is somehow missing.
      const apiErr = e as ApiError;
      set({ error: apiErr.message || 'Login failed. Try again.' });
      throw e;
    }
  },

  signOut: async () => {
    // PR-3: warn before destroying offline-queued mutations. The queue is keyed
    // by driverId, so a sign-out followed by a different driver login would
    // never replay this driver's pending writes.
    try {
      const { hasQueuedItems } = await import('./sync');
      if (await hasQueuedItems()) {
        const proceed = window.confirm(
          'You have unsaved changes that haven\'t synced yet. Sign out anyway? They will be lost.',
        );
        if (!proceed) return;
      }
    } catch {
      // If the sync module fails to load, fall through and sign out anyway.
    }

    // Purge the FCM token server-side BEFORE we lose the auth cookie. If we're
    // offline this will fail — keep going so the user isn't trapped signed-in.
    try {
      await apiFetch('/driver-app/me/push-token', { method: 'DELETE', body: JSON.stringify({}) });
    } catch (e) {
      console.warn('push-token DELETE failed; server-side cleanup skipped', e);
    }

    // Best-effort client-side token revoke. Independent try/catch so a Firebase
    // hiccup can't block the rest of the teardown.
    try {
      const { revokePushTokenLocally } = await import('./fcm');
      await revokePushTokenLocally();
    } catch { /* noop */ }

    try {
      await apiFetch('/driver-auth/logout', { method: 'POST' });
    } catch { /* always proceed */ }
    await signOutFirebase();

    // Wipe device-local state so a different driver on the same device starts
    // clean: ALL IDB stores (including pushNotifications history) and the
    // push-onboarded flag so they get a fresh push prompt.
    try {
      const { clearAllStores } = await import('./idb');
      await clearAllStores();
    } catch (e) {
      console.warn('clearAllStores failed during sign-out', e);
    }
    try {
      const { clearPushOnboarded } = await import('./push-onboarding');
      clearPushOnboarded();
    } catch { /* noop */ }

    set({ status: 'unauthenticated', driver: null, pending: null, error: null });
  },

  clearError: () => set({ error: null }),
}));
